/**
 * Application-wide constants
 */

// =============================================================================
// Feature Flags
// =============================================================================
/**
 * Set to true to re-enable the 3D skeleton viewer in EvaluationResults.
 * The 3D data fetching and backend scoring are unaffected by this flag —
 * only the UI toggle and the SkeletonViewer3D component are hidden.
 * 3D visualization hidden — re-enable when rendering quality improves.
 */
export const ENABLE_3D_VISUALIZATION = true;

// Video constraints
export const VIDEO_CONSTRAINTS = {
  MAX_FILE_SIZE: 500 * 1024 * 1024, // 500MB
  SUPPORTED_FORMATS: ['video/mp4', 'video/webm', 'video/quicktime', 'video/x-msvideo', 'video/avi'],
  SUPPORTED_EXTENSIONS: ['.mp4', '.webm', '.mov', '.avi'],
  DEFAULT_FPS: 30,
};

// Video duration caps (seconds).
//
// MAX_VIDEO_DURATION_SECONDS applies to template reference videos and athlete
// evaluation videos (both go through VideoUploader which calls validateVideoDuration).
//
// MAX_TEACHING_VIDEO_DURATION_SECONDS applies to coaching instruction videos
// uploaded via TeachingVideoUploader (video mode only; audio and paste-notes
// are exempt from the duration check).
//
// These constants must stay in sync with the backend values in
// backend/app/config/paths.py (SPORTFORM_MAX_VIDEO_DURATION and
// SPORTFORM_MAX_TEACHING_VIDEO_DURATION env vars).
export const MAX_VIDEO_DURATION_SECONDS = 60;
export const MAX_TEACHING_VIDEO_DURATION_SECONDS = 120;

// Canvas settings
export const CANVAS_CONFIG = {
  LANDMARK_RADIUS: 4,
  CONNECTION_WIDTH: 2,
  REGION_STROKE_WIDTH: 2,
  REGION_FILL_ALPHA: 0.2,
  ERROR_HIGHLIGHT_ALPHA: 0.4,
};

// Score thresholds
export const SCORE_THRESHOLDS = {
  EXCELLENT: 0.9,
  GOOD: 0.8,
  FAIR: 0.6,
  NEEDS_IMPROVEMENT: 0.4,
};

// Phase detection
export const PHASE_CONFIG = {
  MIN_PHASE_DURATION_FRAMES: 10,
  TRANSITION_SMOOTHING_FRAMES: 5,
  VELOCITY_THRESHOLD: 0.1,
};

// UI constants
export const UI_CONFIG = {
  TOAST_DURATION: 5000,
  DEBOUNCE_DELAY: 300,
  ANIMATION_DURATION: 200,
};

// API endpoints — kept for any consumer that prefers a constant.
// Note: EVALUATE was removed (pointed at the 410 Gone /evaluate/with-explanation
// endpoint). Use the submit-and-poll flow: POST /api/evaluate/submit then
// GET /api/evaluations/jobs/{job_id}.
// Note: TEMPLATES was removed (pointed at non-existent /phases/templates;
// correct path is /api/templates).
export const API_ENDPOINTS = {
  VIDEOS: '/videos',
  ANNOTATIONS: '/annotations',
  PHASES: '/phases',
  POSE_EXTRACTION: '/videos/:id/pose',
};

// Local storage keys
export const STORAGE_KEYS = {
  AUTH_TOKEN: 'authToken',
  USER_PREFERENCES: 'userPreferences',
  RECENT_TEMPLATES: 'recentTemplates',
};

// Region types
export const REGION_TYPES = {
  CIRCLE: 'circle',
  POLYGON: 'polygon',
} as const;

// Error severity levels
export const ERROR_SEVERITY = {
  LOW: 'low',
  MEDIUM: 'medium',
  HIGH: 'high',
} as const;

// Sport categories
export const SPORT_CATEGORIES = [
  'Strength & Conditioning',
  'Combat Sports',
  'Gymnastics & Acrobatics',
  'Dance & Performing Arts',
  'Track & Field',
  'Team Sports',
  'Racket Sports',
  'Aquatics',
  'Martial Arts',
  'Other',
] as const;

// Athlete levels
export const ATHLETE_LEVELS = [
  'beginner',
  'intermediate',
  'advanced',
] as const;

// Movement template types
export const MOVEMENT_TYPES = [
  'squat',
  'lunge',
  'push-up',
  'pull-up',
  'deadlift',
  'bench-press',
  'overhead-press',
  'running',
  'jumping',
  'throwing',
  'other',
] as const;

// Common mistakes by movement type (predefined suggestions for TagInput)
export const COMMON_MISTAKES_BY_MOVEMENT_TYPE: Record<string, string[]> = {
  lunge: [
    'Knee collapse inward',
    'Torso leaning too far forward',
    'Rear knee not bending enough',
    'Front heel lifting off ground',
    'Stepping too short or too long',
    'Hips not lowering to parallel',
  ],
  squat: [
    'Knee cave (valgus collapse)',
    'Heels rising off ground',
    'Excessive forward lean',
    'Not reaching depth (above parallel)',
    'Knees traveling too far past toes',
    'Back rounding at bottom',
  ],
  'push-up': [
    'Hips sagging toward floor',
    'Hips piking upward',
    'Elbows flaring past 90 degrees',
    'Incomplete range of motion',
    'Head dropping or craning forward',
    'Shoulder blade instability',
  ],
  deadlift: [
    'Lower back rounding at setup',
    'Bar drifting away from body',
    'Hips shooting up before shoulders',
    'Hyperextending at lockout',
    'Knees caving inward on ascent',
    'Cervical hyperextension (looking up)',
  ],
  running: [
    'Overstriding (foot landing ahead of center of mass)',
    'Excessive vertical oscillation (bouncing)',
    'Arms crossing midline',
    'Forward trunk lean too pronounced',
    'Insufficient hip extension at push-off',
    'Cadence too low for pace',
  ],
  jumping: [
    'Knees caving on landing',
    'Landing with stiff legs',
    'Asymmetric landing (one-sided)',
    'Forward trunk collapse on landing',
    'Arm swing not coordinated with jump',
    'Inadequate triple extension (ankle, knee, hip)',
  ],
  throwing: [
    'Elbow dropping below shoulder at release',
    'Opening hips too early',
    'Insufficient hip-to-shoulder separation',
    'Releasing too late or too early in arc',
    'Arm-only throw (no hip/trunk rotation)',
    'Front foot landing too closed or open',
  ],
  'pull-up': [
    'Kipping when strict movement is required',
    'Not reaching full extension at bottom',
    'Chin not clearing bar at top',
    'Excessive body swing',
    'Elbows flaring too wide',
    'Shrugging shoulders at top',
  ],
  'bench-press': [
    'Bar path drifting toward face or stomach',
    'Feet leaving the floor',
    'Excessive arch losing bench contact',
    'Bouncing bar off chest',
    'Wrists bending back under load',
    'Uneven bar descent (one side lower)',
  ],
  'overhead-press': [
    'Excessive lower back arch',
    'Bar path drifting too far forward',
    'Elbows flaring forward at setup',
    'Not achieving full lockout overhead',
    'Bar not pressing in straight line',
    'No chin retraction for bar path',
  ],
};

// =============================================================================
// Calibration Take Failure Dimensions
// =============================================================================
/**
 * Coarse failure-dimension vocabulary for calibration takes (label='bad' or
 * 'fail'). Single source of truth for both surfaces that offer it:
 *   - CalibrationTakeUploader.tsx — single-select dropdown at upload time,
 *     including the '' ("general bad form") option.
 *   - CalibrationTakeEditForm.tsx — multi-select checkboxes for editing an
 *     existing take's tags (take-edit-frontend); uses
 *     CALIBRATION_REAL_FAILURE_DIMENSION_OPTIONS below, which drops the ''
 *     sentinel since "no boxes checked" already means "general" for a
 *     multi-select.
 * Kept here (not locally in either component) because a component file can
 * only export primitive constants without tripping the
 * react-refresh/only-export-components lint rule — and because a value two
 * components both depend on belongs in one place, not two copies that can
 * drift (see the 2026-08-09 rule-compiler stitch: 8/10 vocabulary mismatch
 * across two independently-authored surfaces caused a silent zero-rules bug).
 *
 * Values must match the backend's `FAILURE_DIMENSION_GROUPS` keys in
 * backend/app/services/calibration_take_processing.py.
 */
export const CALIBRATION_FAILURE_DIMENSION_OPTIONS = [
  { value: '', label: 'General bad form (all dimensions)' },
  { value: 'motion', label: 'Wrong movement path' },
  { value: 'activity', label: 'Wrong body segment activity' },
  { value: 'angle', label: 'Wrong joint angle' },
] as const;

export const CALIBRATION_REAL_FAILURE_DIMENSION_OPTIONS =
  CALIBRATION_FAILURE_DIMENSION_OPTIONS.filter((opt) => opt.value !== '');

// =============================================================================
// Calibration Take Grade Labels (grade-feel scoring plan, Story 4)
// =============================================================================
/**
 * LEGACY letter-grade vocabulary. As of numeric take-grade entry (backlog:
 * numeric-take-grades), neither CalibrationTakeUploader.tsx nor
 * CalibrationTakeEditForm.tsx offers this letter picker anymore — both now
 * use a 0-100 number input (see utils/gradeNumeric.ts's
 * `gradeNumericToBandLetter()` / `GRADE_LABEL_MIDPOINTS`). This constant is
 * kept only because:
 *   1. It documents the band ranges the numeric scale is built on (the
 *      owner's verbatim bands: A 90-100, B 80-89, C 70-79, D 60-69, F <60).
 *   2. `CalibrationTake.grade_label` (backend) is a real, still-valid field
 *      for takes graded before this change — no data was backfilled/renamed.
 * Values must match the backend's `VALID_GRADE_LABELS` in
 * backend/app/services/calibration_take_processing.py.
 */
export const CALIBRATION_GRADE_LABEL_OPTIONS = [
  { value: '', label: 'No grade' },
  { value: 'A', label: 'A: 90-100' },
  { value: 'B', label: 'B: 80-89' },
  { value: 'C', label: 'C: 70-79' },
  { value: 'D', label: 'D: 60-69' },
  { value: 'F', label: 'F: <60' },
] as const;

// =============================================================================
// Evaluation Rolling Window
// =============================================================================
/**
 * Number of calendar days shown on the eval results listing page.
 * Evaluations older than this are intentionally hidden (their videos will have
 * been purged for free-tier users). The full archive is visible on the athlete
 * dashboard.
 */
export const ROLLING_WINDOW_DAYS = 28;

// =============================================================================
// Timeout Constants (in milliseconds)
// =============================================================================
/**
 * Centralized timeout values used throughout the application.
 * All values are in milliseconds.
 */
export const TIMEOUTS = {
  /** Duration to display error messages before auto-dismissing */
  ERROR_MESSAGE_DISPLAY: 5000,
  /** Time to wait before checking if video has loaded */
  VIDEO_LOAD_CHECK: 3000,
  /** Time to wait before prompting user to manually load video */
  MANUAL_LOAD_PROMPT: 5000,
  /** Duration of the "saved" pulse animation */
  SAVE_PULSE_ANIMATION: 2000,
  /** Duration to show snap indicator on landmark selection */
  SNAP_INDICATOR: 400,
  /** Delay before navigating to a new page */
  NAVIGATION_DELAY: 500,
  /** Time to wait before checking if pose data is ready */
  POSE_DATA_CHECK: 3000,
} as const;

// =============================================================================
// Confidence Thresholds (0-1 scale)
// =============================================================================
/**
 * Confidence thresholds for AI/ML predictions and detections.
 * All values are on a 0-1 scale (0% to 100%).
 */
export const CONFIDENCE = {
  // General thresholds
  /** High confidence threshold (85%) */
  HIGH: 0.85,
  /** Medium confidence threshold (60%) */
  MEDIUM: 0.60,
  /** Low confidence threshold (40%) */
  LOW: 0.40,

  // Specific contexts
  /** Auto-apply without user confirmation (85%) */
  AUTO_APPLY: 0.85,
  /** Needs user review/confirmation (50%) */
  NEEDS_REVIEW: 0.50,

  // Visibility categories for landmarks
  VISIBILITY: {
    /** High visibility threshold for landmarks (70%) */
    HIGH: 0.7,
    /** Medium visibility threshold for landmarks (40%) */
    MEDIUM: 0.4,
    /** Low visibility threshold for landmarks (0%) */
    LOW: 0.0,
  },
} as const;

// =============================================================================
// Landmark Detection
// =============================================================================
/**
 * Configuration for landmark detection and selection.
 */
export const LANDMARK_CONFIG = {
  /** Distance threshold for line proximity detection (pixels) */
  LINE_PROXIMITY_THRESHOLD: 20,
  /** Snap radius for easier landmark selection (pixels) */
  SNAP_RADIUS: 60,
  /** Minimum visibility score required for landmark detection */
  MIN_VISIBILITY: 0.3,
} as const;

// =============================================================================
// UI Snap Thresholds
// =============================================================================
/**
 * Snap thresholds for UI controls like sliders and frame navigation.
 */
export const SNAP_CONFIG = {
  /** Percentage threshold for slider snap points (5%) */
  SLIDER_PERCENT: 0.05,
  /** Frame count threshold for frame navigation snap */
  FRAME_COUNT: 8,
} as const;

// =============================================================================
// Adaptive Guidance Overlay — Field IDs (B-2026-0056)
// =============================================================================
/**
 * Stable `data-field-id` attribute values for the Adaptive Guidance Overlay.
 * Using this constant object prevents typos and provides a single source of
 * truth for both the fields that receive the attribute and the overlay that
 * reads it. Add new entries here before adding the attribute to a component.
 *
 * Studio / RegionAnnotator fields:
 *   BODY_REGION_IMPORTANCE  — importance selector in BodyRegionAnnotation
 *   GOAL_LANDMARK_PICKER    — landmark picker in axis/timing goal panels
 *   PHASE_SELECTOR          — phase scope selector (all / current / range)
 *   ANNOTATION_DESCRIPTION  — annotation description / label field
 *   SCORING_STUDIO_PANEL    — ScoringStudio root panel
 *
 * Evaluation Results / EnhancedEvaluationDisplay fields:
 *   EVAL_SCORE_BREAKDOWN    — the score breakdown / phase scores section
 *   EVAL_PHASE_TAB          — phase tab buttons in the phase timeline
 *   EVAL_VIDEO_COMPARE      — the side-by-side video comparison container
 */
export const FIELD_IDS = {
  // Studio fields
  BODY_REGION_IMPORTANCE: 'body-region-importance',
  GOAL_LANDMARK_PICKER: 'goal-landmark-picker',
  PHASE_SELECTOR: 'phase-selector',
  ANNOTATION_DESCRIPTION: 'annotation-description',
  SCORING_STUDIO_PANEL: 'scoring-studio-panel',
  // Evaluation Results fields
  EVAL_SCORE_BREAKDOWN: 'eval-score-breakdown',
  EVAL_PHASE_TAB: 'eval-phase-tab',
  EVAL_VIDEO_COMPARE: 'eval-video-compare',
} as const;

// localStorage key for guide dismissed state (B-2026-0057)
export const GUIDE_DISMISSED_EVAL_RESULTS_KEY = 'guide-dismissed-eval-results';

// localStorage key for studio guide dismissed state (B-2026-0058)
export const GUIDE_DISMISSED_STUDIO_KEY = 'guide-dismissed-studio';

// localStorage key for the Template Details calibration guide dismissed state
// (calibration-upload-guidance)
export const GUIDE_DISMISSED_TEMPLATE_KEY = 'guide-dismissed-template';
